/**
 * Where the account's messages go, and the one way to change it.
 *
 * The page never sees a chat id. It asks the server for a one-use link, the
 * owner opens it in Telegram and presses Start, and the chat that message came
 * from is the chat that is linked. While a link is open the status is re-read
 * every few seconds, so the panel turns to «Подключено» on its own.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { StatusBadge } from "@/design-system/StatusBadge";
import { readTelegramLink, startTelegramLink, unlinkTelegram } from "@/shared/api";
import type { TelegramLinkResponse } from "@/shared/api";

const KEY = ["notifications", "telegram"] as const;

const LINK =
  "inline-flex min-h-9 items-center rounded-row bg-action-primary px-4 text-body-main font-medium text-text-on-action transition-colors duration-150 hover:bg-action-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring";

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

export function TelegramPanel({ owner }: { readonly owner: boolean }): React.JSX.Element {
  const queries = useQueryClient();
  const [link, setLink] = useState<TelegramLinkResponse | null>(null);
  const status = useQuery({
    queryKey: KEY,
    retry: false,
    // Only while a link is open: that is the one moment the answer changes
    // without this page doing anything.
    refetchInterval: link === null ? false : 3_000,
    queryFn: async () => {
      const { data } = await readTelegramLink();
      if (data === undefined) throw new Error("telegram status unavailable");
      return data;
    },
  });
  const start = useMutation({
    mutationFn: async () => {
      const { data } = await startTelegramLink();
      if (data === undefined) throw new Error("link refused");
      return data;
    },
    onSuccess: (issued) => { setLink(issued); },
  });
  const unlink = useMutation({
    mutationFn: async () => {
      const { response } = await unlinkTelegram();
      if (response?.ok !== true) throw new Error("unlink refused");
    },
    onSuccess: async () => { await queries.invalidateQueries({ queryKey: KEY }); },
  });

  const data = status.data;
  const linked = data?.linked === true;
  const open = link !== null && !linked;

  return (
    <Panel
      title="Уведомления в Telegram"
      testId="telegram-panel"
      trailing={data === undefined ? undefined : (
        <StatusBadge tone={linked ? "ready" : "muted"}>{linked ? "Подключено" : "Не подключено"}</StatusBadge>
      )}
    >
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        {data === undefined ? (
          <p role="status">{status.isError ? "Состояние уведомлений недоступно." : "Загружаю…"}</p>
        ) : linked ? (
          <>
            <p>
              Сообщения о циклах и счёте приходят в ваш Telegram
              {data.linked_at === null ? "" : ` с ${day(data.linked_at)}`}.
            </p>
            {owner && (
              <div>
                <Button onClick={() => { unlink.mutate(); }} disabled={unlink.isPending}>Отключить</Button>
              </div>
            )}
          </>
        ) : (
          <>
            <p>
              Подключите Telegram, чтобы получать сообщения о сделках, циклах и состоянии счёта
              от бота @{data.bot}.
            </p>
            {!data.available ? (
              <p data-testid="telegram-unavailable">Подключение через бота сейчас выключено. Сообщения будут ждать, пока его не включат.</p>
            ) : open ? (
              <div className="flex flex-col gap-2" data-testid="telegram-link">
                <p>Откройте ссылку и нажмите «Start» в чате с ботом. Ссылка действует до {time(link.expires_at)}.</p>
                <div>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className={LINK}>Открыть Telegram</a>
                </div>
              </div>
            ) : owner ? (
              <div>
                <Button variant="primary" onClick={() => { start.mutate(); }} disabled={start.isPending}>Подключить Telegram</Button>
              </div>
            ) : (
              <p data-testid="telegram-owner-only">Подключает владелец счёта под своим логином.</p>
            )}
          </>
        )}
        {(start.isError || unlink.isError) && (
          <p role="alert" className="text-status-warning">Не получилось. Обновите страницу и попробуйте ещё раз.</p>
        )}
      </div>
    </Panel>
  );
}
